import { Check, Download, Pencil, RotateCcw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FileUpload } from '~/components/common/file-upload';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '~/components/ui/sheet';
import { Skeleton } from '~/components/ui/skeleton';
import { useDownloadArmAssignmentCsv } from '~/features/content-enrichment/hooks/use-arm-assignment-report';
import { cn } from '~/utils/cn';

import { useKpis } from '../../hooks/use-config';
import { useExperimentDraft } from '../../hooks/use-experiment-draft';
import { useRestartExperiment } from '../../hooks/use-restart-experiment';
import type { KpiOption } from '../../types/create-experiment.types';
import { ExperimentPlatform, type ExperimentDetail } from '../../types/experiment.types';
import type { AppliedProductFilters } from '../../types/product-metadata.types';
import { adPlatformForConnector, type AdPlatform } from '../../utils/platform-mapping';
import { restartName } from '../../utils/restart-payload';

import { ContentAttributeChips } from './content-attribute-chips';

/** Arm-assignment upload constraints, per the file-upload contract. */
const ARM_FILE_MAX_MB = 5;
const ARM_FILE_TYPES = ['.csv'];

interface Props {
  /** Controls sheet visibility — owned by the detail header's action button. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The running experiment being restarted (from the detail dashboard). */
  experiment: ExperimentDetail;
}

/**
 * Right-side sidebar that previews a source experiment's full configuration
 * (read-only, for parity) and lets the operator attach an optional
 * arm-assignment CSV before creating a brand-new run from it. The original is
 * left untouched. Config is fetched fresh via the full draft DTO (the stripped
 * dashboard `ExperimentDetail` lacks arms / test duration), so this opens lazily
 * on `open`.
 */
export function RestartExperimentSheet({ open, onOpenChange, experiment }: Props) {
  const { t } = useTranslation('experiments');
  const na = t('detailsSheet.notAvailable');
  const [armFile, setArmFile] = useState<File | null>(null);
  // The clone's name — seeded from the source + "(Restart)" suffix and editable
  // via the pencil. Reset to the default on close so each open starts fresh.
  const defaultName = restartName(experiment.name) ?? '';
  const [name, setName] = useState(defaultName);
  const [isEditingName, setIsEditingName] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const restart = useRestartExperiment();
  // Downloads the SOURCE experiment's computed test/control split so the operator
  // can start from it, tweak it, and re-upload as the new run's arm assignment.
  const csvDownload = useDownloadArmAssignmentCsv(experiment.id);

  // Focus the name field when the operator enters edit mode (jsx-a11y forbids
  // the `autoFocus` prop, so focus imperatively instead).
  useEffect(() => {
    if (isEditingName) nameInputRef.current?.focus();
  }, [isEditingName]);

  // Lazy — only fetch the full config once the sheet is open.
  const { data: draft, isLoading, isError } = useExperimentDraft(open ? experiment.id : null);
  const { data: kpis } = useKpis(experiment.platform ?? null);
  const findKpi = (id: string | null): KpiOption | null =>
    id ? (kpis?.find((k) => k.value === id) ?? null) : null;

  // Guard close (and reset the picked file + edited name) while a restart is in
  // flight so the operator can't dismiss the sidebar mid-submit.
  const handleOpenChange = (next: boolean) => {
    if (restart.isPending) return;
    if (!next) {
      setArmFile(null);
      setName(defaultName);
      setIsEditingName(false);
    }
    onOpenChange(next);
  };

  const handleConfirm = () => {
    if (!draft) return;
    // Ad platform: prefer the draft's channel code; fall back to the dashboard
    // platform (Google unless the experiment is explicitly Meta).
    const adPlatform: AdPlatform =
      adPlatformForConnector(draft.channel?.code ?? null) ??
      (experiment.platform === ExperimentPlatform.META ? 'META_ADS' : 'GOOGLE_ADS');
    // Send the edited name when non-blank; otherwise let the mapper's default
    // (source name + suffix) stand.
    const trimmedName = name.trim();
    restart.mutate({
      source: draft,
      adPlatform,
      ...(trimmedName ? { name: trimmedName } : {}),
      ...(armFile ? { armAssignmentFile: armFile } : {}),
    });
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="flex-row items-center gap-3 border-b px-6 py-4 pr-14 text-left">
          <RotateCcw aria-hidden="true" className="text-foreground h-5 w-5 shrink-0" />
          <div className="min-w-0">
            <SheetTitle className="text-base">{t('detail.restart.title')}</SheetTitle>
            <SheetDescription className="mt-0.5 text-xs">
              {t('detail.restart.description')}{' '}
              {/* Reassurance clause reflects the ORIGINAL's current state — only a
                  RUNNING experiment "keeps running"; others are "left untouched". */}
              {t(`detail.restart.untouched.${experiment.status}`, {
                defaultValue: t('detail.restart.untouched.default'),
              })}
            </SheetDescription>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isLoading ? (
            <RestartSkeleton />
          ) : isError || !draft ? (
            <p className="text-muted-foreground py-6 text-sm">{t('detail.restart.loadError')}</p>
          ) : (
            <div className="flex flex-col gap-5">
              {/* New name banner — editable; the one thing that differs from the source. */}
              <div className="border-accent-main/20 bg-accent-100/40 rounded-xl border px-4 py-3">
                <p className="text-muted-foreground text-[11px] font-semibold tracking-wide uppercase">
                  {t('detail.restart.willCreateAs')}
                </p>
                {isEditingName ? (
                  <div className="mt-1 flex items-center gap-2">
                    <Input
                      ref={nameInputRef}
                      value={name}
                      aria-label={t('detail.restart.nameLabel')}
                      className="h-8 text-sm font-semibold"
                      onChange={(e) => {
                        setName(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') setIsEditingName(false);
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('detail.restart.saveName')}
                      className="h-8 w-8 shrink-0 cursor-pointer"
                      onClick={() => {
                        setIsEditingName(false);
                      }}
                    >
                      <Check aria-hidden="true" className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="mt-1 flex items-center gap-2">
                    <p className="text-foreground min-w-0 flex-1 text-sm font-bold wrap-break-word">
                      {name || na}
                    </p>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('detail.restart.editName')}
                      disabled={restart.isPending}
                      className="h-7 w-7 shrink-0 cursor-pointer"
                      onClick={() => {
                        setIsEditingName(true);
                      }}
                    >
                      <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>

              {/* ── Configuration (read-only parity) ── */}
              <section className="flex flex-col">
                <SectionLabel label={t('detail.restart.sections.configuration')} />
                <SummaryRow label={t('detailsSheet.fields.platform')}>
                  {experiment.platform ? t(`platform.${experiment.platform}`) : na}
                </SummaryRow>
                <SummaryRow label={t('detailsSheet.fields.primaryKpi')}>
                  {findKpi(draft.primaryKpi)?.label ?? na}
                </SummaryRow>
                <SummaryRow label={t('detailsSheet.fields.secondaryKpi')}>
                  {findKpi(draft.secondaryKpi)?.label ?? na}
                </SummaryRow>
                <SummaryRow label={t('detailsSheet.fields.trafficSplit')}>
                  {draft.trafficSplit && draft.trafficSplit.length > 0 ? (
                    <span className="flex flex-wrap justify-end gap-1">
                      {draft.trafficSplit.map((p, i) => (
                        <span
                          key={i}
                          className={cn(
                            'rounded px-1.5 py-0.5 font-mono text-xs font-semibold',
                            i === 0
                              ? 'bg-accent-100 text-accent-main'
                              : 'bg-muted text-muted-foreground',
                          )}
                        >
                          {String(p)}%
                        </span>
                      ))}
                    </span>
                  ) : (
                    na
                  )}
                </SummaryRow>
                <SummaryRow label={t('detail.restart.fields.testDuration')}>
                  {draft.testDurationDays != null
                    ? t('detail.restart.durationDays', { count: draft.testDurationDays })
                    : na}
                </SummaryRow>
                <SummaryRow label={t('detailsSheet.fields.parentIdField')}>
                  <span className="bg-muted border-border text-foreground rounded border px-2 py-0.5 font-mono text-xs font-semibold">
                    {draft.parentIdField || na}
                  </span>
                </SummaryRow>
              </section>

              {/* ── Content attributes ── */}
              {draft.contentAttributes && draft.contentAttributes.length > 0 && (
                <section className="flex flex-col">
                  <SectionLabel label={t('detailsSheet.fields.contentAttributes')} />
                  <ContentAttributeChips
                    attributes={draft.contentAttributes}
                    byArm={draft.armContentAttributes}
                  />
                </section>
              )}

              {/* ── Targeting ── */}
              <section className="flex flex-col">
                <SectionLabel label={t('detailsSheet.sections.targeting')} />
                <FilterChips filters={draft.scopingFilters} na={na} />
                {draft.groupingFilter?.name != null && (
                  <>
                    <p className="text-muted-foreground mt-2 mb-1 text-xs">
                      {t('detailsSheet.fields.groupingFilter')} · {draft.groupingFilter.name}
                    </p>
                    <FilterChips filters={draft.groupingFilter.filters} na={na} />
                  </>
                )}
              </section>

              {/* ── Arm assignment upload ── */}
              <section className="flex flex-col">
                <SectionLabel label={t('detail.restart.sections.armAssignment')} />
                <p className="text-muted-foreground mb-2 text-sm leading-5">
                  {t('detail.restart.upload.description', { size: ARM_FILE_MAX_MB })}
                </p>
                {/* Start from the source split: download → tweak → re-upload below. */}
                <Button
                  variant="link"
                  size="sm"
                  className="mb-3 h-auto w-fit cursor-pointer gap-1.5 px-0"
                  disabled={restart.isPending}
                  isLoading={csvDownload.isPending}
                  onClick={() => {
                    csvDownload.mutate();
                  }}
                >
                  <Download aria-hidden="true" className="h-3.5 w-3.5" />
                  {t('detail.restart.upload.downloadCurrent')}
                </Button>
                <FileUpload
                  allowedSizeMb={ARM_FILE_MAX_MB}
                  allowedFileTypes={ARM_FILE_TYPES}
                  maxUploads={1}
                  onRawChange={(files) => {
                    setArmFile(files[0] ?? null);
                  }}
                />
              </section>
            </div>
          )}
        </div>

        <SheetFooter className="flex-row items-center justify-end gap-3 border-t px-6 py-4">
          <Button
            variant="outline"
            className="cursor-pointer"
            disabled={restart.isPending}
            onClick={() => {
              handleOpenChange(false);
            }}
          >
            {t('detail.restart.cancel')}
          </Button>
          <Button
            className="cursor-pointer"
            onClick={handleConfirm}
            disabled={!draft || restart.isPending}
            isLoading={restart.isPending}
          >
            {t('detail.restart.confirm')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// ─── Presentational helpers ──────────────────────────────────────────────────

/** Accent dot + uppercase label that opens each config group. */
function SectionLabel({ label }: { label: string }) {
  return (
    <div className="mb-2.5 flex items-center gap-2">
      <span className="bg-accent-main h-1.5 w-1.5 rounded-full" aria-hidden="true" />
      <span className="text-muted-foreground text-[11px] font-bold tracking-widest uppercase">
        {label}
      </span>
    </div>
  );
}

/** Label + right-aligned value row within a config section. */
function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 px-0.5 py-2">
      <span className="text-muted-foreground shrink-0 text-sm">{label}</span>
      <div className="text-foreground min-w-0 text-right text-sm font-medium">{children}</div>
    </div>
  );
}

/**
 * Renders applied filters as wrapped `key: value` chips (shared shape with the
 * details sheet). Null/empty filters fall back to the not-available label.
 */
function FilterChips({ filters, na }: { filters: AppliedProductFilters | null; na: string }) {
  const entries = filters
    ? Object.entries(filters).filter(
        ([, v]) => v !== null && v !== '' && !(Array.isArray(v) && v.length === 0),
      )
    : [];
  if (entries.length === 0) {
    return <p className="text-muted-foreground px-0.5 py-1 text-sm">{na}</p>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([key, value]) => {
        const display = Array.isArray(value) ? value.join(', ') : String(value ?? '');
        return (
          <span
            key={key}
            className="bg-muted/50 border-border flex items-baseline gap-1.5 rounded-lg border px-2.5 py-1.5"
          >
            <span className="text-muted-foreground font-mono text-[11px]">{key}</span>
            <span className="text-foreground text-sm font-semibold">{display}</span>
          </span>
        );
      })}
    </div>
  );
}

/** Placeholder layout shown while the source config query is in flight. */
function RestartSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-16 w-full rounded-xl" />
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center justify-between gap-4 px-0.5 py-1">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-20" />
        </div>
      ))}
      <Skeleton className="h-36 w-full rounded-2xl" />
    </div>
  );
}
